import React, { useEffect, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { X } from 'lucide-react';
import { getImages } from '@/lib/imageStore';
import { getFullSizeUrl } from '@/lib/cloudinary';
import { useSupabase } from '@/providers/SupabaseProvider';

const Gallery = () => {
    const { t, isRTL } = useLanguage();
    const { supabase } = useSupabase();
    const [activeTab, setActiveTab] = useState('recoveries');
    const [selectedImage, setSelectedImage] = useState(null);

    const [images, setImages] = useState({
        recoveries: [],
        piercings: [],
        sketches: [],
        covers: [],
        hair: [],
    });

    useEffect(() => {
        if (!supabase) return;
        let cancelled = false;

        async function loadGalleryImages() {
            const storedImages = await getImages(supabase);
            if (cancelled) return;
            setImages({
                recoveries: storedImages.kakoos || [],
                piercings: storedImages.peircings || [],
                sketches: storedImages.availablesketches || [],
                covers: storedImages.covers || [],
                hair: storedImages.hair || [],
            });
        }

        loadGalleryImages();
        const interval = setInterval(loadGalleryImages, 30000);

        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, [supabase]);

    const tabs = [
        {
            id: 'recoveries',
            label: t.gallery.tabs.recoveries,
            color: 'mid-green',
        },
        {
            id: 'piercings',
            label: t.gallery.tabs.piercings,
            color: 'mid-purple',
        },
        {
            id: 'sketches',
            label: t.gallery.tabs.sketches,
            color: 'light-yellow',
        },
        {
            id: 'covers',
            label: t.gallery.tabs.covers,
            color: 'mid-purple',
        },
        {
            id: 'hair',
            label: t.gallery.tabs.hair,
            color: 'mid-green',
        },
    ];

    const currentImages = images[activeTab] || [];

    return (
        <section
            id='gallery'
            className='relative bg-[#0a0a0a] py-24 overflow-hidden'
        >
            <div className='top-40 left-10 absolute bg-mid-purple/10 blur-3xl rounded-full w-64 h-64' />
            <div className='right-10 bottom-20 absolute bg-light-pink/10 blur-3xl rounded-full w-48 h-48' />

            <div className='z-10 relative mx-auto px-4 lg:px-8 max-w-7xl'>
                <div className='mb-16 text-center'>
                    <span
                        style={{
                            fontFamily: !isRTL ? 'Space Mono' : 'Arial',
                        }}
                        className={`text-mid-purple text-sm uppercase tracking-[0.2em]`}
                    >
                        {t.gallery.subtitle}
                    </span>
                    <h2 className='mt-4 font-display text-white text-4xl lg:text-6xl uppercase'>
                        {t.gallery.title}
                    </h2>
                </div>

                <div className='flex flex-wrap justify-center gap-4 mb-12'>
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`px-6 py-3 rounded-full  text-sm uppercase tracking-wider transition-all duration-300 ${
                                activeTab === tab.id
                                    ? `bg-${tab.color} text-black`
                                    : 'border border-white/20 text-white hover:border-light-pink/50'
                            }`}
                            style={{
                                backgroundColor:
                                    activeTab === tab.id
                                        ? tab.color === 'mid-purple'
                                            ? '#d987ff'
                                            : tab.color === 'mid-green'
                                              ? '#78d692'
                                              : '#f6fd87'
                                        : 'transparent',
                            }}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                <div className='gap-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'>
                    {currentImages.map((image, index) => (
                        <div
                            key={image.public_id || image.id}
                            onClick={() => setSelectedImage(image)}
                            className='group relative rounded-lg aspect-square overflow-hidden cursor-pointer'
                            style={{ animationDelay: `${index * 100}ms` }}
                        >
                            <img
                                src={image.src}
                                alt={image.alt}
                                className='w-full h-full object-cover group-hover:scale-110 transition-all duration-500'
                            />
                            <div className='absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300' />
                            <div className='absolute inset-0 flex justify-center items-center opacity-0 group-hover:opacity-100 transition-opacity duration-300'>
                                <span className='bg-white/90 px-4 py-2 rounded-full text-black text-xs uppercase tracking-wider'>
                                    {isRTL ? 'צפה' : 'View'}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {selectedImage && (
                <div
                    className='z-50 fixed inset-0 flex justify-center items-center bg-black/95 p-4'
                    onClick={() => setSelectedImage(null)}
                >
                    <button
                        onClick={() => setSelectedImage(null)}
                        className='top-6 right-6 absolute text-white hover:text-light-pink transition-colors'
                    >
                        <X className='w-8 h-8' />
                    </button>
                    <img
                        src={getFullSizeUrl(selectedImage.src)}
                        alt={selectedImage.alt}
                        className='rounded-lg max-w-full max-h-[85vh] object-contain'
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            )}
        </section>
    );
};

export default Gallery;
